import { useState } from 'react';
import { Header, Card, Button, Badge, StatCard } from '../../shared/components';
import { UsersTab } from '../settings/components/UsersTab';
import { RateFactorsTab } from './components/RateFactorsTab';
import { CalendarTab } from './components/CalendarTab';
import { TemporalCompaniesTab } from './components/TemporalCompaniesTab';
import { PositionsTab } from './components/PositionsTab';
import { ThemeSwitcher } from '../../shared/theme/ThemeSwitcher';
import { useCurrentUser, useSignOut } from '../../shared/auth/AuthProvider';
import { UsersIcon, SettingsIcon, TimerIcon, DatabaseIcon, UsersIcon as CompanyIcon, DashboardIcon as PositionIcon } from '../../shared/icons';

const ROLE_LABEL: Record<string, string> = { admin: 'Administrador', supervisor: 'Supervisor', operator: 'Operador' };

type RootView = 'root' | 'usuarios' | 'parametros';
type ParametrosView = 'root' | 'calendario' | 'factores' | 'temporales' | 'cargos';

/**
 * Configuracion de Gestion Humana. Navegacion en dos niveles:
 *  Configuracion -> [Usuarios | Parametros]
 *  Parametros    -> [Calendario | Factores Legales]
 *
 * "Usuarios" reutiliza UsersTab (ya generico, ver shared/auth/ApplicationContext).
 * "Parametros" es nuevo: Calendario (equivalente a DIAS de PARAMETROS del
 * Excel) y Factores Legales (equivalente a la fila 3 de RESUMEN, ahora con
 * CRUD para que la analista los ajuste si la ley cambia).
 */
export function HrSettingsPage() {
  const [rootView, setRootView] = useState<RootView>('root');
  const [parametrosView, setParametrosView] = useState<ParametrosView>('root');

  if (rootView === 'usuarios') {
    return (
      <div>
        <Header title="Usuarios" subtitle="Gestión Humana" onBack={() => setRootView('root')} />
        <div className="p-4">
          <UsersTab />
        </div>
      </div>
    );
  }

  if (rootView === 'parametros') {
    if (parametrosView === 'calendario') {
      return (
        <div>
          <Header title="Calendario" subtitle="Parámetros de Gestión Humana" onBack={() => setParametrosView('root')} />
          <div className="p-4">
            <CalendarTab />
          </div>
        </div>
      );
    }
    if (parametrosView === 'factores') {
      return (
        <div>
          <Header title="Factores Legales" subtitle="Parámetros de Gestión Humana" onBack={() => setParametrosView('root')} />
          <div className="p-4">
            <RateFactorsTab />
          </div>
        </div>
      );
    }
    if (parametrosView === 'temporales') {
      return (
        <div>
          <Header title="Empresas Temporales" subtitle="Parámetros de Gestión Humana" onBack={() => setParametrosView('root')} />
          <div className="p-4">
            <TemporalCompaniesTab />
          </div>
        </div>
      );
    }
    if (parametrosView === 'cargos') {
      return (
        <div>
          <Header title="Cargos" subtitle="Parámetros de Gestión Humana" onBack={() => setParametrosView('root')} />
          <div className="p-4">
            <PositionsTab />
          </div>
        </div>
      );
    }
    return (
      <div>
        <Header title="Parámetros" subtitle="Gestión Humana" onBack={() => setRootView('root')} />
        <div className="grid grid-cols-2 gap-3 p-4">
          <StatCard icon={<DatabaseIcon />} label="Días, tipo de día y quincenas" value="Calendario" tone="info" onClick={() => setParametrosView('calendario')} />
          <StatCard icon={<TimerIcon />} label="Divisores y % de recargo" value="Factores Legales" tone="brand" onClick={() => setParametrosView('factores')} />
          <StatCard icon={<CompanyIcon />} label="Selector 'Temporal' de Empleados" value="Empresas Temporales" tone="info" onClick={() => setParametrosView('temporales')} />
          <StatCard icon={<PositionIcon />} label="Selector 'Cargo' de Empleados" value="Cargos" tone="brand" onClick={() => setParametrosView('cargos')} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Configuración" subtitle="Gestión Humana" />
      <div className="space-y-6 p-4">
        <SessionCard />
        <div className="grid grid-cols-2 gap-3">
          <StatCard icon={<UsersIcon />} label="Roles y acceso" value="Usuarios" tone="brand" onClick={() => setRootView('usuarios')} />
          <StatCard icon={<SettingsIcon />} label="Calendario y factores legales" value="Parámetros" tone="info" onClick={() => setRootView('parametros')} />
        </div>
      </div>
    </div>
  );
}

function SessionCard() {
  const user = useCurrentUser();
  const signOut = useSignOut();

  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-ink-800">{user.fullName}</p>
          <div className="mt-1">
            <Badge tone="info">{ROLE_LABEL[user.role] ?? user.role} · Gestión Humana</Badge>
          </div>
        </div>
        {signOut && (
          <Button variant="outline" size="md" onClick={signOut}>
            Cerrar sesión
          </Button>
        )}
      </div>
      <div className="mt-4 border-t border-ink-100 pt-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">Tema visual</p>
        <ThemeSwitcher />
      </div>
    </Card>
  );
}
